import { ChevronDown, UtensilsCrossed } from "lucide-react";
import { motion } from "framer-motion";
import heroImage from "@/assets/menu/PATACONES/ESPECIAL.webp";
import logo from "../../assets/logo.svg";

const container = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.15, delayChildren: 0.3 },
  },
};

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] as const },
  },
} as const;

const scaleIn = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { type: "spring" as const, stiffness: 200, damping: 20 },
  },
} as const;

const lineGrow = {
  hidden: { scaleX: 0 },
  visible: {
    scaleX: 1,
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] as const },
  },
} as const;

export default function MenuHero() {
  const scrollToMenu = () => {
    document.getElementById("menu-nav")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="hero"
      className="relative flex items-center justify-center overflow-hidden"
      style={{ minHeight: "100svh" }}
    >
      {/* Background image */}
      <motion.img
        src={heroImage}
        alt="Plato destacado de El Verde Restaurante — comida costeña ecuatoriana"
        className="absolute inset-0 w-full h-full object-cover object-center"
        fetchPriority="high"
        draggable={false}
        initial={{ scale: 1.15 }}
        animate={{ scale: 1.05 }}
        transition={{ duration: 1.8, ease: [0.22, 1, 0.36, 1] }}
      />

      {/* Multi-layer overlay for depth */}
      <div className="absolute inset-0 bg-gradient-to-b from-primary-dark/80 via-primary-dark/50 to-primary-dark/90" />
      <div className="absolute inset-0 bg-gradient-to-r from-primary-dark/40 via-transparent to-primary-dark/40" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_40%,rgba(0,0,0,0.4))]" />

      {/* Content */}
      <motion.div
        className="relative z-10 text-center px-4 sm:px-8 max-w-3xl mx-auto"
        variants={container}
        initial="hidden"
        animate="visible"
      >
        {/* Logo icon */}
        <motion.div className="flex justify-center mb-6" variants={scaleIn}>
          <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-accent/90 flex items-center justify-center shadow-2xl shadow-accent/30 backdrop-blur-sm border-2 border-accent-light/50">
            <img src={logo} alt="" className="w-12 h-12 sm:w-14 sm:h-14" />
          </div>
        </motion.div>

        {/* Pre-heading */}
        <motion.div
          className="flex items-center justify-center gap-4 mb-4"
          variants={fadeUp}
        >
          <motion.span
            className="h-px w-10 sm:w-16 bg-accent/50 origin-right"
            variants={lineGrow}
          />
          <span className="text-accent text-xs sm:text-sm font-bold tracking-[0.3em] uppercase">
            Comida Costeña Ecuatoriana · Cuenca
          </span>
          <motion.span
            className="h-px w-10 sm:w-16 bg-accent/50 origin-left"
            variants={lineGrow}
          />
        </motion.div>

        {/* Main title */}
        <motion.h1
          className="font-heading font-black text-white leading-[0.9] mb-6 drop-shadow-2xl"
          style={{ fontSize: "clamp(3.5rem, 13vw, 8rem)" }}
          variants={fadeUp}
        >
          El Verde
        </motion.h1>

        {/* Tagline */}
        <motion.p
          className="text-white/80 text-lg sm:text-xl md:text-2xl font-light leading-relaxed mb-12 max-w-lg mx-auto"
          variants={fadeUp}
        >
          Caldos, mariscos y sabores de la Costa
          <br className="hidden sm:block" />
          <span className="text-accent font-medium">
            traídos al corazón de Cuenca.
          </span>
        </motion.p>

        {/* CTA button */}
        <motion.button
          onClick={scrollToMenu}
          className="group inline-flex items-center gap-2.5 bg-accent hover:bg-accent-light text-primary-dark font-extrabold px-10 py-4 rounded-full text-base shadow-2xl shadow-accent/40 hover:shadow-accent/60 transition-colors duration-200 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-accent"
          variants={fadeUp}
          whileHover={{ y: -2 }}
          whileTap={{ scale: 0.97 }}
          transition={{ type: "spring", stiffness: 400, damping: 17 }}
        >
          <UtensilsCrossed className="w-5 h-5 transition-transform duration-200 group-hover:rotate-12" />
          Ver la Carta
        </motion.button>
      </motion.div>

      {/* Scroll indicator */}
      <motion.div
        className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1.5 text-white/40"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 0.6 }}
      >
        <span className="text-[10px] tracking-[0.3em] uppercase font-semibold">
          Desliza
        </span>
        <motion.div
          animate={{ y: [0, 6, 0] }}
          transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
        >
          <ChevronDown className="w-5 h-5" />
        </motion.div>
      </motion.div>

      {/* Decorative bottom curve */}
      <div
        className="absolute bottom-0 left-0 right-0 h-20"
        style={{
          background:
            "linear-gradient(to top, var(--color-sand) 40%, transparent)",
        }}
      />
    </section>
  );
}
